import { useImmer } from "use-immer";
import { useContext, useEffect, useRef, useState } from "react";
import SimpleReactValidator from "simple-react-validator";
import { useTipsAndTricksQuery } from "./useTipsAndTricksQuery";
import { useDebounce } from "use-debounce";
import { useDelete, usePermission } from "../../../shared";
import { AppContext } from "../../../store";
import { useCategoryQueries } from "../../MasterData/hooks/useCategoryQueries";
import { useCreateOrEditQueries } from "../../Recipe/hooks";

export const useTipsAndTricks = (props) => {
  const isDeletable = usePermission("Tips&Tricks-delete");
  const isCreateVisible = usePermission("Tips&Tricks-create");
  const isEditable = usePermission("Tips&Tricks-edit");

  const { isList = false, recipeId, tipId, isRecipeTipsList = false } = props;
  const [, forceUpdate] = useState();
  const validator = useRef(
    new SimpleReactValidator({ autoForceUpdate: { forceUpdate: forceUpdate } }),
  );
  const [tipsAndTricks, setTipsAndTricks] = useImmer({
    type: 0,
    category_type: 0,
    file: [],
    data: {
      type: "Tips & Tricks",
      name: "",
      method: "",
      recipe_id: [],
      category_id: [],
      doc_url: [],
      steps: [
        {
          step: "",
          image_url: "",
          imageFile: "",
          loading: false,
        },
      ],
    },
    search: "",
    url: [],
    media: "",
    videoUrl: "",
    filters: {
      categoryfilter: "",
      type: "",
      all: [],
    },
    open: false,
    items: [],
    index: null,
  });

  const { deleteInfo, onDelete, resetDeleteInfo } = useDelete();
  const moduleName = "tipsAndTricks";
  const {
    tableData,
    setSorting,
    setPagination,
    setSearchState,
    setTableState,
    globalData,
    clearAllFilters,
  } = useContext(AppContext);
  const { sorting, pagination, search } = tableData[moduleName];
  const onChangeStatusHandler = (value) => {
    setTableState({
      tableName: moduleName,
      key: "selectedType",
      value: value,
    });
    setTipsAndTricks((draft) => {
      draft.filters.type = value;
    });
  };
  const selectTypeOptions = globalData?.tips_procedures_types.map((item) => ({
    label: item.label,
    value: item.value,
  }));

  const [debouncedText] = useDebounce(
    isRecipeTipsList ? search : tipsAndTricks?.search,
    1000,
  );
  const {
    listQuery,
    recipeListQuery,
    deleteTipsAndTricks,
    createTipsAndTricks,
    tipsAndTricksDetails,
    updateTipsAndTricks,
    createImageFileUrl,
  } = useTipsAndTricksQuery({
    isList,
    pagination,
    debouncedText,
    recipeId,
    sorting,
    resetDeleteInfo,
    tipId,
    filterApplied: tableData?.tipsAndTricks?.data?.categoryfilter,
    type: tableData?.tipsAndTricks?.data?.selectedType,
    tipsAndTricks,
  });
  const { listQuery: categoryListQuery } = useCategoryQueries({
    categorylist: true,
  });
  const CategoryOptions = categoryListQuery?.data?.map((v) => {
    return { id: v.id, name: v.name };
  });
  const { createImageVideoUrl } = useCreateOrEditQueries({
    list: false,
    ingredientslist: false,
    altunitlist: false,
    isIngredientsApiEnabled: false,
    setTipsAndTricks,
    tipsAndTricks,
  });
  const confirmDelete = () => {
    deleteTipsAndTricks.mutate({
      id: deleteInfo.id,
      to: deleteInfo.to,
      isList,
    });
  };
  useEffect(() => {
    if (!tipsAndTricks.data.type) {
      setTipsAndTricks((draft) => {
        draft.data.type = draft.data.type || "Tips & Tricks";
      });
    }
  }, [tipsAndTricks.data.type]);
  const onChange = (e, type) => {
    if (type === "type") {
      setTipsAndTricks((draft) => {
        draft.type = tipsAndTricks.type === 1 ? 0 : 1;
      });
    } else if (type === "category_type") {
      setTipsAndTricks((draft) => {
        draft.category_type = tipsAndTricks.category_type === 1 ? 0 : 1;
      });
    } else {
      const { name, value } = e.target;
      setTipsAndTricks((draft) => {
        draft.data[name] = value;
      });
    }
  };
  useEffect(() => {
    if (tipsAndTricksDetails?.data?.recipe_id?.length) {
      setTipsAndTricks((draft) => {
        draft.type = 1;
      });
    }
  }, [tipsAndTricksDetails?.data?.recipe_id]);

  const onSearchHandler = (search) => {
    if (isRecipeTipsList) {
      setSearchState({
        tableName: moduleName,
        key: "search",
        value: search,
      });
      setPagination({
        tableName: moduleName,
        callback: (prevPagination) => ({
          ...prevPagination,
          pageIndex: 1,
        }),
      });
    } else {
      setTipsAndTricks((draft) => {
        draft.search = search;
      });
    }
  };

  useEffect(() => {
    if (createImageFileUrl?.data) {
      setTipsAndTricks((draft) => {
        if (!Array.isArray(draft.data.doc_url)) {
          draft.data.doc_url = [];
        }
        draft.data.doc_url = [...draft.data.doc_url, createImageFileUrl.data];
      });
    }
  }, [createImageFileUrl.data]);

  const onSubmit = () => {
    if (validator.current.allValid()) {
      const {
        name,
        recipe_id,
        doc_url = [],
        category_id,
        type,
        method,
        steps = [],
      } = tipsAndTricks.data;

      const formData = new FormData();
      formData.append("name", name ?? "");
      formData.append(
        "type",
        type === "Tips & Tricks" ? "tips_and_tricks" : "procedures",
      );
      formData.append("method", method ?? "");

      (recipe_id ?? []).forEach((item) => {
        formData.append("recipe_id[]", item?.id ?? "");
      });

      (category_id ?? []).forEach((item) => {
        formData.append("category_id[]", item?.id ?? "");
      });

      steps.map((item, index) => {
        if (item?.step?.length || item?.image_url?.length) {
          formData.append(`steps[${index}][step]`, item?.step ?? []);
          formData.append(`steps[${index}][image_url]`, item?.image_url ?? []);
        } else {
          formData.append(`steps[${index}][step]`, []);
          formData.append(`steps[${index}][image_url]`, []);
        }
      });
      if (doc_url.length) {
        doc_url.map((url, index) => {
          formData.append(
            `doc_url[${index}]`,
            url?.url ? url?.url : (url?.doc_url ?? ""),
          );
        });
      } else {
        formData.append("doc_url", []);
      }
      if (tipId) {
        updateTipsAndTricks.mutate({ tipId, formData });
      } else {
        createTipsAndTricks.mutate({ formData });
      }
    } else {
      validator.current.showMessages();
      forceUpdate(1);
    }
  };

  useEffect(() => {
    if (tipsAndTricksDetails?.data) {
      const details = tipsAndTricksDetails.data;
      const type = details?.recipes?.length ? 1 : 0;
      const categoryType = details?.categories?.length ? 1 : 0;
      const recipe_id = details?.recipes?.map((v) => ({
        id: v.recipe_id,
        name: v.recipe_name,
      }));

      const category_id = details?.categories?.map((v) => ({
        id: v.category_id,
        name: v.category_name,
      }));

      const steps = details?.steps.map((v) => ({
        step: v.steps,
        image_url: v.image_url,
      }));

      setTipsAndTricks((draft) => {
        draft.isLoading = false;
        draft.type = type;
        draft.category_type = categoryType;
        draft.data.type = details?.type;
        draft.data.name = details?.name;
        draft.data.method = details.method;
        draft.data.doc_url = tipsAndTricksDetails?.data?.images;
        if (category_id) draft.data.category_id = category_id;
        if (recipe_id) draft.data.recipe_id = recipe_id;
        if (steps) draft.data.steps = steps;
      });
    }
    // setTipsAndTricks((draft) => {
    //   draft.isLoading = false;
    //   draft.type = tipsAndTricksDetails?.data?.recipes?.length ? 1 : 0;
    //   draft.category_type = tipsAndTricksDetails?.data?.categories?.length
    //     ? 1
    //     : 0;
    //   draft.data = {
    //     ...tipsAndTricksDetails?.data,
    //     doc_url: tipsAndTricksDetails?.data?.images,
    //     recipe_id: tipsAndTricksDetails?.data?.recipes?.map((v) => {
    //       return { id: v.recipe_id, name: v.recipe_name };
    //     }),
    //     category_id: tipsAndTricksDetails?.data?.categories?.map((v) => {
    //       return { id: v.category_id, name: v.category_name };
    //     }),
    //     steps: tipsAndTricksDetails?.data?.steps?.map((v) => {
    //       return { step: v.steps, image_url: v.image_url };
    //     }),
    //   };
    // });
  }, [tipsAndTricksDetails?.data]);
  const onDeleteRecipeSelect = (data) => {
    setTipsAndTricks((draft) => {
      draft.data.recipe_id = tipsAndTricks.data.recipe_id.filter((value) => {
        return value.id !== data.id;
      });
    });
  };
  const handleInputChangeFile = (e) => {
    const imageFile = e.target.files[0];
    let type = "";
    if (e.target.files[0]?.type.includes("image")) {
      type = "image";
    } else if (e?.target?.files[0]?.type?.includes("pdf")) {
      type = "doc";
    } else {
      type = "tips_video";
    }
    setTipsAndTricks((draft) => {
      draft.file = imageFile;
    });

    let imageFormdata = new FormData();
    imageFormdata.append("type", type);
    imageFormdata.append("media", imageFile);
    createImageFileUrl.mutate(imageFormdata);
  };
  const onDeleteFileSelect = (removeIndex) => {
    setTipsAndTricks((draft) => {
      if (Array.isArray(draft.data.doc_url)) {
        draft.data.doc_url = draft.data.doc_url.filter(
          (_, idx) => idx !== removeIndex,
        );
      }

      if (Array.isArray(draft.file)) {
        draft.file = draft.file.filter((_, idx) => idx !== removeIndex);
      } else {
        draft.file = "";
      }
    });
  };

  const typeRadioButtonChange = (type) => {
    setTipsAndTricks((draft) => {
      draft.data.type = type;
    });
  };

  const handleQuillChange = (value) => {
    setTipsAndTricks((draft) => {
      draft.data.method = value;
    });
  };
  const handleDragEnd = (result) => {
    const { source, destination } = result;

    if (!destination) return;

    const reorderedSteps = Array.from(tipsAndTricks.data.steps);
    const [movedStep] = reorderedSteps.splice(source.index, 1);
    reorderedSteps.splice(destination.index, 0, movedStep);
    setTipsAndTricks((draft) => {
      draft.data.steps = reorderedSteps;
    });
  };
  const handleDropzone = (e, index) => {
    const imageFile = e[0];
    setTipsAndTricks((draft) => {
      draft.data.steps[index].imageFile = imageFile;
      draft.data.steps[index].loading = true;
      draft.index = index;
    });

    let imageFormdata = new FormData();
    imageFormdata.append("type", "image");
    imageFormdata.append("media", imageFile);
    createImageVideoUrl.mutate(imageFormdata);

    indexRef.current = index;
  };
  const indexRef = useRef();
  useEffect(() => {
    if (createImageVideoUrl?.data) {
      const imageIndex = indexRef.current;
      setTipsAndTricks((draft) => {
        draft.data.steps[imageIndex].image_url = createImageVideoUrl?.data?.url;
        draft.data.steps[imageIndex].loading = false;
      });
    }
  }, [createImageVideoUrl.data]);
  const addStepChangeHandler = () => {
    const newRow = {
      step: "",
      formattedStep: "",
      image_url: "",
    };

    setTipsAndTricks((draft) => {
      // Ensure draft.data exists
      if (!draft.data) {
        draft.data = {};
      }

      // Ensure steps array is initialized
      if (!Array.isArray(draft.data.steps)) {
        draft.data.steps = [];
      }

      draft.data.steps.push(newRow);
    });
  };

  const removeImage = (index) => {
    setTipsAndTricks((draft) => {
      draft.data.steps[index].image_url = "";
      draft.data.steps[index].imageFile = "";
    });
  };

  const handleStepChange = (content, index) => {
    setTipsAndTricks((draft) => {
      const step = draft.data.steps[index];
      if (step && step.step !== content) {
        step.step = content;
        step.formattedStep = content.replace(/<\/?[^>]+(>|$)/g, ""); // Non-render logic
      }
    });
  };
  const stepsHandleDelete = (index) => {
    setTipsAndTricks((draft) => {
      draft.data.steps = [...draft.data.steps.filter((data, i) => i !== index)];
    });
  };
  const categoryValuesHandler = (event, option) => {
    const item = option?.map((item) => {
      return { ...item };
    });
    setTableState({
      tableName: moduleName,
      key: "categoryfilter",
      value: item,
    });
    setTipsAndTricks((draft) => {
      draft.filters.categoryfilter = "";
    });
  };

  const onClearFilter = () => {
    clearAllFilters({ tableName: moduleName });
  };
  const selectedType = tableData?.tipsAndTricks?.data?.selectedType;
  const categoryFilter = tableData?.tipsAndTricks?.data?.categoryfilter;
  const disableclearButton =
    !selectedType &&
    (!Array.isArray(categoryFilter) || categoryFilter.length === 0);
  const handleVideoClick = (type, item) => {
    setTipsAndTricks((draft) => {
      draft.open = type;
      draft.items = item;
    });
  };
  const onImageClick = (item) => {
    setTipsAndTricks((draft) => {
      draft.items = item;
    });
  };

  const onDeleteCategorySelect = (data) => {
    setTipsAndTricks((draft) => {
      draft.data.category_id = tipsAndTricks.data.category_id.filter(
        (value) => {
          return value.id !== data.id;
        },
      );
    });
  };

  return {
    tipsAndTricks,
    listQuery,
    pagination,
    setPagination,
    onChange,
    validator,
    recipeListQuery,
    onSearchHandler,
    sorting,
    setSorting,
    onDelete,
    confirmDelete,
    deleteInfo,
    resetDeleteInfo,
    onSubmit,
    tipsAndTricksDetails,
    onDeleteRecipeSelect,
    handleInputChangeFile,
    onDeleteFileSelect,
    updateTipsAndTricks,
    createTipsAndTricks,
    createImageFileUrl,
    isCreateVisible,
    isEditable,
    isDeletable,
    moduleName,
    search,
    typeRadioButtonChange,
    handleQuillChange,
    categoryListQuery,
    handleDragEnd,
    handleDropzone,
    addStepChangeHandler,
    removeImage,
    handleStepChange,
    stepsHandleDelete,
    onChangeStatusHandler,
    tableData,
    selectTypeOptions,
    CategoryOptions,
    categoryValuesHandler,
    onClearFilter,
    disableclearButton,
    handleVideoClick,
    onImageClick,
    onDeleteCategorySelect,
  };
};
